import {
  Morpheme_OutOfVocab,
  Sense,
  Sentence
} from '#/protobuf/bareun/language_service_pb';
import { Fragment, useEffect, useState } from 'react';
import { getPOSTag, getTagColor, getTagKorea } from '@bareun/morphemeviewer';
import { Card, CardContent } from '#/components/ui/card';
import { Button } from '#/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '#/components/ui/tooltip';
import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import {
  countExpandedInRange,
  phraseSpans,
  totalRenderedRows
} from './table-layout';
import {
  formatSenseNo,
  formatSenseProbability,
  senseApiNotation,
  senseMeaning,
  senseTooltipText,
  urimalDictUrl
} from './sense';

interface ParsedToken {
  showIndex: number;
  ptext: string;
  text: string;
  tag: string;
  ktag: string;
  outOfVocab: string;
  probability: string;
  modified: string;
  beginOffset: number;
  length: number;
  /** WSD 결과. 의미가 부여되지 않은 형태소(조사·어미 등)에는 없다. */
  sense?: Sense;
}

interface ColorTexts {
  firstTexts: string;
  colorTexts: string;
  lastTexts: string;
  bColor: string;
  tColor?: string;
}

export default function AnalyzeTable({
  sentences,
  withSense = false
}: {
  sentences: Sentence[] | any;
  withSense?: boolean;
}) {
  const [parsedTokens, setParsedTokens] = useState<ParsedToken[][]>([]);
  // 펼쳐진 의미 상세 행. 표를 넓히지 않기 위해 상세는 별도 행으로만 보여준다.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [lengthIdx, setLengthIdx] = useState<number[]>([]);
  const [showIndex, setShowIndex] = useState<number | null>(null);
  const [activeString, setActiveString] = useState<string | null>(null);
  const [colorTexts, setColorTexts] = useState<ColorTexts>({
    firstTexts: '',
    colorTexts: '',
    lastTexts: '',
    bColor: '',
    tColor: ''
  });

  const parseTokens = (sentences: Sentence[] | any) => {
    const result: ParsedToken[][] = [];
    const lengthIndexArray: number[] = [];

    for (let sIdx = 0; sIdx < sentences.length; sIdx++) {
      const sentence = sentences[sIdx];
      const tokenList = sentence?.tokensList || sentence?.tokens;
      const parsedRow: ParsedToken[] = [];
      let count = 0;

      for (const token of tokenList) {
        const ptextObj = token?.text;
        const morphemesList = token?.morphemesList || token?.morphemes;
        const modified = token.modified;

        for (const morpheme of morphemesList) {
          const outOfVocabLabel =
            //@ts-ignore
            {
              [Morpheme_OutOfVocab.OUT_OF_VOCAB]: '신조어',
              [Morpheme_OutOfVocab.IN_WORD_EMBEDDING]: '학습 사전',
              [Morpheme_OutOfVocab.IN_BUILTIN_DICT]: '내장 사전',
              [Morpheme_OutOfVocab.IN_CUSTOM_DICT]: '사용자 사전',
              [Morpheme_OutOfVocab.IN_URIMALSAEM]: '우리말샘',
              [Morpheme_OutOfVocab.IN_WIKI_DICT]: '위키 사전',
              [Morpheme_OutOfVocab.IN_ON_DICT]: '온용어 사전',
              ['OUT_OF_VOCAB']: '신조어',
              ['IN_WORD_EMBEDDING']: '학습 사전',
              ['IN_BUILTIN_DICT']: '내장 사전',
              ['IN_CUSTOM_DICT']: '사용자 사전',
              ['IN_URIMALSAEM']: '우리말샘',
              ['IN_WIKI_DICT']: '위키 사전',
              ['IN_ON_DICT']: '온용어 사전'
            }[morpheme?.outOfVocab] || '알 수 없음';

          const probab = (+morpheme.probability * 100)
            .toFixed(2)
            .replace(/\.00$/, '');

          parsedRow.push({
            showIndex: sIdx,
            ptext: ptextObj?.content ?? '',
            text: morpheme?.text?.content ?? '',
            tag: getPOSTag(morpheme.tag),
            ktag: getTagKorea(getPOSTag(morpheme.tag)),
            outOfVocab: outOfVocabLabel,
            probability: probab,
            modified: modified ?? '',
            beginOffset: morpheme?.text?.beginOffset ?? 0,
            length: morpheme?.text?.length ?? 0,
            sense: morpheme?.sense
          });

          count++;
        }
      }

      result.push(parsedRow);
      lengthIndexArray.push(parsedRow.length);
    }

    setParsedTokens(result);
    setLengthIdx(lengthIndexArray);
    setColorTexts({
      firstTexts: '',
      colorTexts: '',
      lastTexts: sentences[0]?.text?.content ?? '',
      bColor: '',
      tColor: ''
    });
  };

  const handleMouseEnter = (data: ParsedToken, id: string) => {
    setShowIndex(data.showIndex);
    setActiveString(id);

    const sentence = sentences[data.showIndex];
    const sentenceText = sentence.text?.content ?? '';
    const sentenceBeginOffset = sentence.text?.beginOffset ?? 0;

    const relativeOffset = data.beginOffset - sentenceBeginOffset;

    setColorTexts({
      firstTexts: sentenceText.slice(0, relativeOffset),
      colorTexts: sentenceText.slice(
        relativeOffset,
        relativeOffset + data.length
      ),
      lastTexts: sentenceText.slice(relativeOffset + data.length),
      bColor: getTagColor(data.tag)
    });
  };

  const handleMouseLeave = () => {
    if (showIndex === null) return;
    setColorTexts({
      firstTexts: '',
      colorTexts: '',
      lastTexts: sentences[showIndex]?.text?.content ?? '',
      bColor: '',
      tColor: ''
    });
    setActiveString(null);
  };

  // 어절 묶기(ptext 가 같은 연속 행)는 table-layout.ts 의 phraseSpans 가 담당한다.
  // 펼쳐진 상세 행까지 rowSpan 에 반영해야 해서 그쪽으로 옮겼다.

  useEffect(() => {
    parseTokens(sentences);
    setExpandedIds(new Set());
  }, [sentences]);

  // 의미가 부여된 형태소의 행 id 목록. 조사·어미엔 원래 sense 가 붙지 않으므로
  // 대개 전체 행보다 훨씬 적다.
  const senseIds = parsedTokens.flatMap((group, sIdx) =>
    group.flatMap((item, tIdx) => (item.sense ? [`${sIdx}-${tIdx}`] : []))
  );
  const hasAnySense = senseIds.length > 0;

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allExpanded = hasAnySense && expandedIds.size === senseIds.length;

  return (
    <div className="flex flex-col  flex-1 h-full gap-4 ">
      <div className="">
        <span>{colorTexts.firstTexts}</span>
        <span
          style={{ color: colorTexts.tColor, background: colorTexts.bColor }}
        >
          {colorTexts.colorTexts}
        </span>
        <span>{colorTexts.lastTexts}</span>
      </div>

      {/* 의미 구분을 켰을 때만 안내·일괄 펼침을 보여준다 */}
      {withSense && (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          {hasAnySense ? (
            <>
              <span className="text-xs text-muted-foreground">
                형태 옆 어깨번호에 마우스를 올리면 뜻풀이가 보이고, 누르면 상세가
                펼쳐집니다. (의미 {senseIds.length}개)
              </span>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                onClick={() =>
                  setExpandedIds(allExpanded ? new Set() : new Set(senseIds))
                }
              >
                {allExpanded ? '의미 모두 접기' : '의미 모두 펼치기'}
              </Button>
            </>
          ) : (
            <span className="text-xs text-muted-foreground">
              이 문장에서 구분된 의미가 없습니다. 동형이의어가 없거나, 서버가 아직
              의미 구분(베타)을 지원하지 않는 버전일 수 있습니다.
            </span>
          )}
        </div>
      )}

      <Card className="py-2 flex-1 h-full overflow-auto ">
        <CardContent className="px-1">
          <div className="p-3 w-full">
            {lengthIdx.length > 0 && (
              <table className="w-full">
                <colgroup>
                  <col width="5%" />
                  <col width="20%" />
                  <col width="10%" />
                  <col width="10%" />
                  <col width="20%" />
                  <col width="10%" />
                  <col width="10%" />
                </colgroup>
                <thead>
                  <tr className="border-b text-sm">
                    <td>번호</td>
                    <td>어절</td>
                    <td>형태</td>
                    <td>태그</td>
                    <td>품사</td>
                    <td>확률(%)</td>
                    <td>출처</td>
                  </tr>
                </thead>
                {parsedTokens.map((group, sentenceIdx) => {
                  // 펼쳐진 상세 행이 끼어들면 번호·어절 셀의 rowSpan 도 그만큼
                  // 늘려야 표가 어긋나지 않는다(table-layout.ts 에서 불변식 테스트).
                  const expandedRows = new Set(
                    group.flatMap((_, i) =>
                      expandedIds.has(`${sentenceIdx}-${i}`) ? [i] : []
                    )
                  );
                  const spans = phraseSpans(
                    group.map((it) => it.ptext),
                    expandedRows
                  );
                  const spanByStart = new Map(
                    spans.map((sp) => [sp.startIdx, sp.rowSpan])
                  );
                  const numberRowSpan = totalRenderedRows(
                    group.length,
                    countExpandedInRange(expandedRows, 0, group.length)
                  );

                  return (
                    <tbody key={sentenceIdx}>
                      {group.map((item, tokenIdx) => {
                        const id = `${sentenceIdx}-${tokenIdx}`;
                        const prev = group[tokenIdx - 1];
                        const next = group[tokenIdx + 1];
                        const isFirstOfPhrase =
                          !prev || item.ptext !== prev.ptext;
                        const isLastOfPhrase =
                          !next || item.ptext !== next.ptext;
                        const isExpanded = expandedIds.has(id);

                        const meaning = item.sense
                          ? senseMeaning(item.sense)
                          : null;
                        const urimalUrl = item.sense
                          ? urimalDictUrl(item.sense.urimalTargetId)
                          : null;

                        return (
                          <Fragment key={id}>
                            <tr>
                              {tokenIdx === 0 && (
                                <td
                                  rowSpan={numberRowSpan}
                                  className="border-b align-top text-sm"
                                >
                                  {sentenceIdx + 1}
                                </td>
                              )}
                              {isFirstOfPhrase && (
                                <td
                                  rowSpan={spanByStart.get(tokenIdx) ?? 1}
                                  onMouseEnter={() =>
                                    handleMouseEnter(item, id)
                                  }
                                  onMouseLeave={handleMouseLeave}
                                  className="border-b align-top text-sm"
                                  style={{
                                    backgroundColor:
                                      activeString === id ? '#cdcdcd' : ''
                                  }}
                                >
                                  {item.ptext}
                                  {item.modified && <p>({item.modified})</p>}
                                </td>
                              )}

                              {/* 형태 — 어깨번호 배지를 이 칸에 붙여 열을 늘리지 않는다 */}
                              <td
                                onMouseEnter={() => handleMouseEnter(item, id)}
                                onMouseLeave={handleMouseLeave}
                                className={
                                  isLastOfPhrase && !isExpanded
                                    ? 'border-b text-sm'
                                    : 'text-sm'
                                }
                                style={{
                                  backgroundColor:
                                    activeString === id ? '#cdcdcd' : ''
                                }}
                              >
                                <span className="inline-flex items-center gap-1">
                                  {item.text}
                                  {item.sense && (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <button
                                          type="button"
                                          onClick={() => toggleExpanded(id)}
                                          aria-expanded={isExpanded}
                                          aria-label={`의미 ${formatSenseNo(
                                            item.sense.senseNo
                                          )} 상세 ${isExpanded ? '접기' : '펼치기'}`}
                                          className="inline-flex items-center gap-0.5 rounded bg-primary/10 px-1 text-[10px] font-medium text-primary hover:bg-primary/20"
                                        >
                                          {isExpanded ? (
                                            <ChevronDown className="size-2.5" />
                                          ) : (
                                            <ChevronRight className="size-2.5" />
                                          )}
                                          {formatSenseNo(item.sense.senseNo)}
                                        </button>
                                      </TooltipTrigger>
                                      <TooltipContent
                                        side="top"
                                        className="max-w-xs"
                                      >
                                        {senseTooltipText(
                                          item.text,
                                          item.sense
                                        )}
                                      </TooltipContent>
                                    </Tooltip>
                                  )}
                                </span>
                              </td>

                              {['tag', 'ktag', 'probability', 'outOfVocab'].map(
                                (field) => (
                                  <td
                                    key={field}
                                    onMouseEnter={() =>
                                      handleMouseEnter(item, id)
                                    }
                                    onMouseLeave={handleMouseLeave}
                                    className={
                                      isLastOfPhrase && !isExpanded
                                        ? 'border-b text-sm'
                                        : 'text-sm'
                                    }
                                    style={{
                                      backgroundColor:
                                        activeString === id ? '#cdcdcd' : ''
                                    }}
                                  >
                                    {(item as any)[field]}
                                  </td>
                                )
                              )}
                            </tr>

                            {/* 의미 상세 — 번호·어절 칸은 위 rowSpan 이 덮으므로 5칸만 쓴다.
                                긴 뜻풀이를 표 폭에 밀어넣지 않으려고 별도 행으로 뺐다. */}
                            {isExpanded && item.sense && (
                              <tr className="bg-muted/40">
                                <td
                                  colSpan={5}
                                  className={
                                    isLastOfPhrase
                                      ? 'border-b px-2 py-2 align-top'
                                      : 'px-2 py-2 align-top'
                                  }
                                >
                                  <div className="flex flex-col gap-1 text-xs">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <code className="rounded bg-background px-1 py-0.5 font-mono">
                                        {senseApiNotation(
                                          item.text,
                                          item.sense.senseNo
                                        )}
                                      </code>
                                      <span className="text-muted-foreground">
                                        선택 확률{' '}
                                        {formatSenseProbability(
                                          item.sense.probability
                                        )}
                                        %
                                      </span>
                                      {urimalUrl && (
                                        <a
                                          href={urimalUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="inline-flex items-center gap-0.5 text-primary hover:underline"
                                        >
                                          우리말샘
                                          <ExternalLink className="size-3" />
                                        </a>
                                      )}
                                    </div>
                                    <p className="leading-relaxed">
                                      {meaning ?? (
                                        <span className="text-muted-foreground">
                                          뜻풀이가 등록되지 않은 의미입니다.
                                        </span>
                                      )}
                                    </p>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  );
                })}
              </table>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
